import * as React from "react";
import { useCallback, useMemo } from "react";
import cloneDeep from "lodash/cloneDeep";
import * as Immutable from "immutable";
import styled, { css } from "styled-components";
import { defaultCompare as naturalSort, defaultCompare } from "logic/DefaultCompare";
import { Select } from "components/common";
import { Col, ControlLabel, FormGroup, HelpBlock, Row } from "components/bootstrap";
import { percentileOptions, percentageStrategyOptions } from "views/Constants";
import { Properties } from "views/logic/fieldtypes/FieldType";
import FieldTypeIcon from "views/components/sidebar/fields/FieldTypeIcon";
const formatFunctions = (functions) => functions.sort(naturalSort).map((fn) => ({ label: `${fn.toLowerCase()}()`, value: fn }));
const getSeriesId = (currentSeries) => {
  var _a;
  return `${currentSeries.type}-${(_a = currentSeries.field) != null ? _a : ""}`;
};
const FieldName = styled.span`
  display: inline-flex;
  gap: 2px;
  align-items: center;
`;
const UnqualifiedOption = styled.span(
  ({ theme }) => css`
    color: ${theme.colors.gray[70]};
  `
);
const optionRenderer = ({ label, qualified, type = void 0 }) => {
  const children = /* @__PURE__ */ React.createElement(FieldName, null, type && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(FieldTypeIcon, { type }), " "), label);
  return qualified ? /* @__PURE__ */ React.createElement("span", null, children) : /* @__PURE__ */ React.createElement(UnqualifiedOption, null, children);
};
const sortByLabel = ({ label: label1 }, { label: label2 }) => defaultCompare(label1, label2);
const NumberRefExpression = ({
  aggregationFunctions,
  formattedFields,
  eventDefinition,
  expression,
  onChange,
  renderLabel,
  validation = {}
}) => {
  var _a, _b;
  const getSeries = useCallback(
    (seriesId) => {
      var _a2, _b2;
      return (_b2 = (_a2 = eventDefinition == null ? void 0 : eventDefinition.config) == null ? void 0 : _a2.series) == null ? void 0 : _b2.find((series2) => series2.id === seriesId);
    },
    [(_a = eventDefinition == null ? void 0 : eventDefinition.config) == null ? void 0 : _a.series]
  );
  const createSeries = useCallback(() => ({ id: expression.ref }), [expression.ref]);
  const getOrCreateSeries = useCallback((seriesId) => getSeries(seriesId) || createSeries(), [createSeries, getSeries]);
  const handleFieldChange = useCallback(
    (key, value) => {
      var _a2;
      const series2 = cloneDeep((_a2 = eventDefinition == null ? void 0 : eventDefinition.config) == null ? void 0 : _a2.series);
      const nextSeries = cloneDeep(getOrCreateSeries(expression.ref));
      if (value !== void 0) {
        nextSeries[key] = value;
      }
      if (key === "type" && value !== "percentage") {
        delete nextSeries.strategy;
      }
      if (key === "type" && value !== "percentage") {
        delete nextSeries.percentile;
      }
      const nextSeriesId = getSeriesId(nextSeries);
      nextSeries.id = nextSeriesId;
      const seriesIndex = series2.findIndex((s) => s.id === nextSeries.id);
      if (seriesIndex >= 0) {
        series2[seriesIndex] = nextSeries;
      } else {
        series2.push(nextSeries);
      }
      const nextExpression = cloneDeep(expression);
      nextExpression.ref = nextSeriesId;
      onChange({
        series: series2,
        conditions: nextExpression
      });
    },
    [(_b = eventDefinition == null ? void 0 : eventDefinition.config) == null ? void 0 : _b.series, expression, getOrCreateSeries, onChange]
  );
  const handleAggregationFunctionChange = useCallback(
    (nextFunction) => {
      handleFieldChange("type", nextFunction);
    },
    [handleFieldChange]
  );
  const handleAggregationFieldChange = useCallback(
    (nextField) => {
      const normalizedField = nextField === "" ? null : nextField;
      handleFieldChange("field", normalizedField);
    },
    [handleFieldChange]
  );
  const series = getSeries(expression.ref) || {};
  const elements = ["percentage", "percentile"].includes(series.type) ? 3 : 2;
  const hasProperty = (fieldType, properties) => {
    var _a2, _b2;
    const fieldProperties = (_b2 = (_a2 = fieldType == null ? void 0 : fieldType.type) == null ? void 0 : _a2.properties) != null ? _b2 : Immutable.Set();
    return properties.map((property) => fieldProperties.contains(property)).find((result) => result === false) === void 0;
  };
  const currentFunction = series.type;
  const isPercentage = currentFunction === "percentage";
  const requiresNumericField = isPercentage && series.strategy === "SUM" || !["card", "count", "latest", "percentage"].includes(currentFunction);
  const isFieldQualified = useCallback(
    (field) => {
      if (!requiresNumericField) {
        return true;
      }
      return hasProperty(field, [Properties.Numeric]);
    },
    [requiresNumericField]
  );
  const fieldOptions = useMemo(
    () => formattedFields.map((field) => ({
      label: `${field.name}`,
      value: field.name,
      type: field.value.type,
      qualified: isFieldQualified(field)
    })).sort(sortByLabel),
    [isFieldQualified, formattedFields]
  );
  return /* @__PURE__ */ React.createElement(Col, { md: 6 }, /* @__PURE__ */ React.createElement(FormGroup, { controlId: "aggregation-function", validationState: validation.message ? "error" : null }, renderLabel && /* @__PURE__ */ React.createElement(ControlLabel, null, "If"), /* @__PURE__ */ React.createElement(Row, { className: "row-sm" }, /* @__PURE__ */ React.createElement(Col, { md: 12 / elements }, /* @__PURE__ */ React.createElement(
    Select,
    {
      className: "aggregation-function",
      placeholder: "Select Function",
      onChange: handleAggregationFunctionChange,
      options: formatFunctions(aggregationFunctions),
      clearable: false,
      value: series.type
    }
  )), series.type === "percentage" && /* @__PURE__ */ React.createElement(Col, { md: 12 / elements }, /* @__PURE__ */ React.createElement(
    Select,
    {
      className: "aggregation-function-strategy",
      placeholder: "Select Strategy (Optional)",
      onChange: (newValue) => handleFieldChange("strategy", newValue),
      options: percentageStrategyOptions,
      value: series.strategy
    }
  )), /* @__PURE__ */ React.createElement(Col, { md: 12 / elements }, /* @__PURE__ */ React.createElement(
    Select,
    {
      className: "aggregation-function-field",
      ignoreAccents: false,
      placeholder: "Select Field (Optional)",
      onChange: handleAggregationFieldChange,
      options: fieldOptions,
      optionRenderer,
      value: series.field,
      allowCreate: true
    }
  )), series.type === "percentile" && /* @__PURE__ */ React.createElement(Col, { md: 12 / elements }, /* @__PURE__ */ React.createElement(
    Select,
    {
      className: "aggregation-function-percentile",
      placeholder: "Select Percentile",
      onChange: (newValue) => handleFieldChange("percentile", newValue),
      options: percentileOptions,
      value: series.percentile
    }
  ))), validation.message && /* @__PURE__ */ React.createElement(HelpBlock, null, validation.message)));
};
export default NumberRefExpression;
